import { type IGetMessagesSms, useGetMessages, useSendMessage } from "@api";
import type { ICandidate } from "@bbj-types/candidate";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { Button, Grid, Typography } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { useGlobalContext } from "@root/context";
import { SmsSentStatus, SmsType } from "@root/utils/enums/communication";
import { formatPhoneNumber } from "@root/utils/phoneLib";
import { Authorizer, Input, ListVirtualized, SmsInfoPopover, SpeechBubble, useAuthorizer } from "@styleguide";
import { type Dispatch, type SetStateAction, forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { VirtuosoHandle } from "react-virtuoso";
import { getMessageAuthor } from "./business-logics/message.drawer.logic";
import { smsIcons } from "./message.status.constant";
import { MessageBBJDrawer, StatusIcon, classes } from "./styles";

export interface IMessageStatusIconProps {
  status: SmsSentStatus;
}

const MessageStatusIcon = ({ status }: IMessageStatusIconProps) => {
  const iconProps = smsIcons[status];
  return <StatusIcon color={iconProps?.color}>{iconProps?.icon}</StatusIcon>;
};

const SpeechBubbleSkeleton = () => {
  return (
    <>
      <Grid
        container
        sx={{
          justifyContent: "flex-start",
        }}
        size={12}>
        <Grid sx={{ minWidth: "83.333333%" }} size={10}>
          <Skeleton animation="wave" className={classes.skeleton} />
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          justifyContent: "flex-end",
        }}
        size={12}>
        <Grid sx={{ minWidth: "83.333333%" }} size={10}>
          <Skeleton animation="wave" className={classes.skeleton} />
        </Grid>
      </Grid>
    </>
  );
};
export interface IViewStateManager {
  list: IGetMessagesSms[];
  total: number | null;
  skip: number | null;
  scrollToBottom: boolean;
}

interface ISMSDrawerProps {
  candidate: Pick<ICandidate, "_id" | "firstName" | "lastName" | "phone">;
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export const SmsDrawer = forwardRef<HTMLDivElement, ISMSDrawerProps>(function SmsDrawer(
  { candidate, isOpen, setOpen },
  ref
) {
  const { user } = useGlobalContext();
  const isAuthorizedSendSms = useAuthorizer(["candidate.sms.send"]);
  const [, getMessages] = useGetMessages();
  const [{ loading: sendLoading }, sendSms] = useSendMessage();
  const [messageToSend, setMessageToSend] = useState("");
  const [state, setState] = useState<IViewStateManager>({
    list: [],
    total: null,
    skip: null,
    scrollToBottom: false,
  });
  const scrollRef = useRef<VirtuosoHandle>();
  const [smsInfo, setSmsInfo] = useState<{ anchorEl: HTMLInputElement | null; info: IGetMessagesSms | null }>({
    anchorEl: null,
    info: null,
  });

  const handleSendSms = useCallback(async () => {
    if (!candidate?.phone) {
      window.publish("feedback", {
        severity: "error",
        message: "Vous n'avez pas renseigné de numéro de téléphone pour ce candidat",
      });
    } else {
      const response = await sendSms({
        message: messageToSend.trim(),
        type: SmsType.SENT,
        candidate: candidate._id,
        phone: candidate.phone,
        sentStatus: SmsSentStatus.PENDING,
      });
      if (response.status === 201) {
        if (response.data) {
          setState((state) => ({
            list: [
              {
                ...response.data,
                createdBy: { _id: user._id, firstName: user.firstName, lastName: user.lastName },
                createdAt: new Date(),
                updatedAt: new Date(),
                candidate: { _id: candidate._id, firstName: candidate.firstName, lastName: candidate.lastName },
                apply: undefined,
              },
              ...state.list,
            ],
            skip: state.skip ? state.skip + 1 : 1,
            total: state.total ? state.total + 1 : 1,
            scrollToBottom: true,
          }));
          setMessageToSend("");
        }
      }
    }
  }, [candidate._id, candidate.phone, messageToSend, sendSms, user._id, user.name]);

  const rowRenderer = useCallback(
    (index: number, message: IGetMessagesSms) => {
      if (!message) {
        return null;
      }
      return (
        <div style={{ paddingRight: "30px" }}>
          <SpeechBubble
            key={`message-${index}`}
            direction={message.type}
            onClick={(e) => setSmsInfo({ anchorEl: e.currentTarget, info: message })}
            details={
              <Typography className={classes.speechBubbleDetail}>
                {`${new Date(message.createdAt).toLocaleDateString("fr-FR")} à ${new Date(message.createdAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} ${candidate && getMessageAuthor(candidate, message.createdBy, message.type)}`}
                {message.sentStatus && <MessageStatusIcon status={message.sentStatus} />}
              </Typography>
            }
          >
            {message.message}
          </SpeechBubble>
        </div>
      );
    },
    [candidate]
  );

  const loadRows = useCallback(async () => {
    const response = await getMessages({
      candidate: candidate._id,
      skip: 0,
      limit: 20,
      sort: encodeURIComponent('{"createdAt":-1}'),
    });
    if (response.data?.data) {
      setState({
        list: [...response.data.data],
        skip: response.data.count ? response.data.count : null,
        total: response.data.total ? response.data.total : null,
        scrollToBottom: false,
      });
    }
  }, [candidate, getMessages]);

  const loadMoreRows = useCallback(async () => {
    if (state.skip) {
      const response = await getMessages({
        candidate: candidate._id,
        skip: state.skip,
        limit: 20,
        sort: encodeURIComponent('{"createdAt":-1}'),
      });
      const sms = response?.data?.data;
      if (response.data && sms) {
        setState((state) => ({
          list: [...state.list, ...sms],
          skip: response.data.count && state.skip ? state.skip + response.data.count : null,
          total: response.data.total ? response.data.total : null,
          scrollToBottom: false,
        }));
      }
    }
  }, [candidate, state.skip, getMessages]);

  useEffect(() => {
    if (candidate && isOpen) {
      loadRows();
    } else {
      setState({ list: [], skip: null, total: null, scrollToBottom: false });
    }
  }, [candidate, isOpen]);

  useEffect(() => {
    if (state.scrollToBottom && scrollRef.current) {
      scrollRef.current.scrollToIndex({
        index: state.list.length,
        align: "start",
        behavior: "auto",
      });
      setState((state) => ({ ...state, scrollToBottom: false }));
    }
  }, [state.scrollToBottom]);

  return (
    <MessageBBJDrawer
      ref={ref}
      id="conversations-messages-drawer"
      data-hj-suppress="true"
      isOpen={isOpen}
      setOpen={setOpen}
      onClose={() => {
        setOpen(false);
        setMessageToSend("");
      }}
      TitleComponent={() => (
        <div className={classes.title}>
          <Grid container>
            <Grid size={12}>
              <Link to={`/v2/candidate/${candidate._id}`} target="_blank">
                <Typography variant="h2">
                  {candidate?.firstName || ""} {candidate?.lastName || ""}
                </Typography>
              </Link>
            </Grid>
            {candidate?.phone && (
              <Grid size={12}>
                <Typography variant="h4">{formatPhoneNumber(candidate.phone)}</Typography>
              </Grid>
            )}
          </Grid>
        </div>
      )}
      PaperContentProps={{
        sx: { display: "flex", flexDirection: "column-reverse" },
      }}
    >
      <Grid id="message-drawer-content" container className={classes.messagesSection} sx={{ flexDirection: "column" }}>
        {/* MESSAGES DISPLAY */}

        {state.total && state.total > 0 ? (
          <ListVirtualized
            ref={scrollRef}
            data={state.list}
            itemContent={rowRenderer}
            infinite={{
              getter: loadMoreRows,
              total: state.total,
              skeleton: SpeechBubbleSkeleton,
            }}
            reverse
          />
        ) : (
          <div />
        )}
        {isAuthorizedSendSms && (
          <Grid
            container
            className={classes.sendMessageWrapper}
            sx={{
              justifyContent: "flex-end",
            }}
            size={12}>
            <Grid
              container
              sx={{
                justifyContent: "flex-end",
              }}
              size={12}>
              <Input
                id="messages-send-content"
                data-testid="messages-send-content"
                placeholder="Saisissez votre message"
                variant="outlined"
                value={messageToSend}
                onChange={(evt) => setMessageToSend(evt.target.value)}
                color="primary"
                type="text"
                multiline
                rows={6}
                fullWidth
                InputProps={{
                  className: classes.sendTextbox,
                }}
                FormHelperTextProps={{
                  children: `${messageToSend?.trim().length || 0}/160`,
                  color: "primary",
                  style: { textAlign: "right" },
                }}
                helperText={`${messageToSend?.trim().length}/160 caractère${
                  messageToSend?.trim().length === 1 ? "" : "s"
                }`}
                disabled={sendLoading}
              />
            </Grid>
            <Grid className={classes.sendButton} size={12}>
              <Button
                id="messages-send-btn"
                data-testid="messages-send-btn"
                onClick={handleSendSms}
                color="primary"
                variant="contained"
                name="confirm"
                aria-labelledby="validate"
                size="small"
                endIcon={<SendOutlinedIcon />}
                disabled={
                  messageToSend?.trim().length === 0 || sendLoading || !user?.organisation?.features?.sms?.active
                }
              >
                Envoyer
              </Button>
            </Grid>
          </Grid>
        )}
        {smsInfo?.info?.errorCode && smsInfo.info.sentStatus !== SmsSentStatus.SENT && (
          <Authorizer userRole={user?.roleV2} requiredPermissions={[]} isSuper>
            <SmsInfoPopover
              sx={{ zIndex: 1400 }}
              anchorEl={smsInfo.anchorEl}
              onClose={() => setSmsInfo({ ...smsInfo, anchorEl: null })}
              info={smsInfo.info}
            />
          </Authorizer>
        )}
      </Grid>
    </MessageBBJDrawer>
  );
});
